import { styled } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormTextField, SubmitButton } from '../../components';
import { ErrorResponse } from 'types/network';
import { Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMswManager } from 'lib/util/useMswManager';
import { HrznGatewayMswBuilder } from 'stories/HrznGatewayMswBuilder';
import { ROUTES } from 'constants/routes';
import { useMutation } from 'react-query';
import { useAuthenticatedAxios } from 'hooks/useAuthenticatedAxios';

const Layout = styled('div')`
  max-width: 2048px;
  margin: 0 auto;
  padding: 0 30px;
`;

interface FormValues {
  email: string;
  lastName: string;
}

function ForgotPassword() {
  const { mutateAsync: resetPassword } =
    usePartnerUpSertExternalUserPasswordResetRequest();

  const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email().required('Required'),
    lastName: Yup.string().required('Required')
  });

  async function _handleSubmit(values: FormValues, { resetForm }) {
    const { email, lastName } = values;
    await resetPassword({ email, lastName });
    resetForm();
  }

  return (
    <Layout>
      <Paper
        elevation={2}
        style={{
          maxWidth: '400px',
          margin: 'auto',
          marginTop: '20px',
          display: 'flex',
          flexDirection: 'column',
          padding: '40px'
        }}
      >
        <Typography variant="h4">
          Partner Portal <br /> Password Recovery
        </Typography>
        <br />
        <Typography color="textSecondary" variant="body2">
          Please enter your email and last name. An email will be sent to you to
          reset your password.
        </Typography>
        <Formik<FormValues>
          onSubmit={_handleSubmit}
          initialValues={{
            email: '',
            lastName: ''
          }}
          validationSchema={ForgotPasswordSchema}
          render={({ isValidating, isSubmitting }) => {
            return (
              <Form
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '15px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <FormTextField
                    style={{ height: '76px' }}
                    idx="forgotPassword-email"
                    name="email"
                    label="Email"
                    isRequired={true}
                  />
                  <FormTextField
                    style={{ height: '76px' }}
                    idx="forgotPassword-lastName"
                    name="lastName"
                    label="Last Name"
                    isRequired={true}
                  />
                  <SubmitButton
                    size="large"
                    id="myAccount-submit"
                    loading={!!(isValidating || isSubmitting)}
                    disabled={!!(isValidating || isSubmitting)}
                  >
                    Submit
                  </SubmitButton>
                </div>
              </Form>
            );
          }}
        />
      </Paper>
    </Layout>
  );
}

export default ForgotPassword;
interface PartnerUpSertExternalUserPasswordResetRequestPayload {
  email: string;
  lastName: string;
}
function usePartnerUpSertExternalUserPasswordResetRequest() {
  const Axios = useAuthenticatedAxios();
  const snackbar = useSnackbar();

  useMswManager(
    HrznGatewayMswBuilder.create().post(
      ROUTES.PartnerUpSertExternalUserPasswordResetRequest,
      {
        data: 'success'
      }
    )
  );

  return useMutation<
    unknown,
    ErrorResponse,
    PartnerUpSertExternalUserPasswordResetRequestPayload
  >(
    async ({ email, lastName }) => {
      const { data } = await Axios({
        method: 'post',
        url: '',
        data: { email, lastName },
        params: {
          route: ROUTES.PartnerUpSertExternalUserPasswordResetRequest
        }
      });
      return data;
    },
    {
      onSuccess: () => {
        snackbar.enqueueSnackbar(
          'Check your inbox for a password reset email.',
          { variant: 'success' }
        );
      },
      onError: err => {
        snackbar.enqueueSnackbar(
          err.response.data.Error ||
            ROUTES.PartnerUpSertExternalUserPasswordResetRequest,
          { variant: 'error' }
        );
      }
    }
  );
}
